import { simpleDecrypt } from "../../../utils/crypto/simple-encrypt-decrypt.js";
import { Alerts } from "../../../utils/Alerts.js";
import { normalizeRpcError } from "../../../utils/rpc-error.js";
import { rpc } from "../rpc-bridge.js";
import { AuthService } from "../services/AuthService.js";
import { AuthEventBus } from "../events/AuthEvents.js";
import { useLocalStorage } from "../../../hooks/useLocalStorage.js";
import { getDevice } from "@therms/web-js";
import { ViewModel, singleton } from "mvc-kit";
//#region src/modules/AuthWrapper2/viewmodels/AuthViewModel.ts
/**
* Auth procedures that run before a session exists. A 401 from one of these is a
* rejected sign-in attempt, not an expired session.
*/
var PRE_AUTH_PROCEDURES = /* @__PURE__ */ new Set([
	"authenticate",
	"authenticate-as-user",
	"request-reset-password",
	"set-new-password"
]);
var LS_AUTH_TOKEN_KEY = "authToken";
var LS_AUTH_TOKEN_PENDING_KEY = "authTokenPending";
var LS_IDENTITY_KEY = "identity";
var AuthViewModel = class extends ViewModel {
	static DEFAULT_STATE = {
		authToken: "",
		identityInfo: null,
		switchingUserProfile: false
	};
	authService = singleton(AuthService);
	bus = singleton(AuthEventBus);
	persistAuthToken = true;
	app = {
		appName: "",
		appVersion: ""
	};
	get isAuthenticated() {
		return !!this.state.authToken && !!this.state.identityInfo;
	}
	get currentUser() {
		return this.state.identityInfo?.user ?? null;
	}
	get currentOrganization() {
		return this.state.identityInfo?.organization ?? null;
	}
	get regions() {
		return this.state.identityInfo?.regions ?? [];
	}
	onInit() {
		this.setupResponseInterceptor();
		this.setupServerEventHandlers();
		this.setupCrossTabSync();
		const godModeToken = this.consumeGodModeTokenFromUrl();
		if (godModeToken) {
			this.emit("godModeTokenApplied", void 0);
			this.clearPendingAuthToken();
			this.set({
				authToken: godModeToken,
				identityInfo: null
			});
		} else this.hydrateFromStorage();
	}
	onSet(prev, next) {
		if (prev.authToken !== next.authToken && this.persistAuthToken) useLocalStorage.setItem(LS_AUTH_TOKEN_KEY, next.authToken || void 0);
		if (prev.identityInfo !== next.identityInfo) useLocalStorage.setItem(LS_IDENTITY_KEY, next.identityInfo || void 0);
		if (next.authToken && prev.authToken !== next.authToken) this.validateSession();
		if (next.identityInfo && prev.identityInfo !== next.identityInfo) {
			this.bus.emit("identityChanged", next.identityInfo);
			this.emit("identityChanged", next.identityInfo);
		}
	}
	async validateSession() {
		const { rpcClient } = rpc();
		const device = getDevice();
		rpcClient.setIdentity({
			authorization: this.state.authToken,
			deviceName: device?.name
		});
		try {
			const data = await this.authService.getIdentityInfo();
			this.set({
				identityInfo: data,
				switchingUserProfile: false
			});
			this.silentTokenRefresh();
			this.registerDevice();
		} catch (rpcResponse) {
			if (!window.navigator.onLine) {
				Alerts.error("No internet connection", "Please check your internet connection");
				return;
			}
			console.warn("Error auth::get-identity-info", normalizeRpcError(rpcResponse).detail);
			this.clearAuth();
		}
	}
	async changeUser(userId) {
		if (this.state.switchingUserProfile) return;
		this.set({ switchingUserProfile: true });
		try {
			const data = await this.authService.switchUserProfile(userId);
			if (data.authToken) {
				this.clearPendingAuthToken();
				this.set({ authToken: data.authToken });
			} else {
				Alerts.error("There was a problem switching profiles", "no authToken provided");
				this.clearAuth();
			}
		} catch (err) {
			this.clearAuth();
			this.emit("changeUserFailed", {
				appName: this.app.appName,
				errorMessage: err.message
			});
		} finally {
			this.set({ switchingUserProfile: false });
		}
	}
	logout() {
		this.authService.deAuthenticate();
		this.clearAuth();
		this.bus.emit("loggedOut", void 0);
		this.emit("loggedOut", { appName: this.app.appName });
	}
	setAuthToken(token) {
		const { rpcClient } = rpc();
		const device = getDevice();
		rpcClient.setIdentity({
			authorization: token,
			deviceName: device?.name
		});
		this.clearPendingAuthToken();
		this.set({ authToken: token });
	}
	setApp(appInfo) {
		this.app = appInfo;
	}
	hydrateFromStorage() {
		let storedToken = "";
		let pendingToken = "";
		try {
			storedToken = useLocalStorage.getItem(LS_AUTH_TOKEN_KEY) || "";
		} catch {}
		try {
			pendingToken = useLocalStorage.getItem(LS_AUTH_TOKEN_PENDING_KEY) || "";
		} catch {}
		let storedIdentity = null;
		try {
			storedIdentity = useLocalStorage.getItem(LS_IDENTITY_KEY) || null;
		} catch {}
		const token = pendingToken || storedToken;
		if (token) {
			this.persistAuthToken = token === storedToken;
			this.set({
				authToken: token,
				identityInfo: storedIdentity
			});
			this.persistAuthToken = true;
		}
	}
	consumeGodModeTokenFromUrl() {
		const urlParams = new URLSearchParams(window.location.search);
		const urlAuthToken = urlParams.get("authToken");
		if (!urlAuthToken) return null;
		urlParams.delete("authToken");
		const newSearch = urlParams.toString();
		window.history.replaceState({}, "", newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname);
		return simpleDecrypt(urlAuthToken);
	}
	setupResponseInterceptor() {
		const { rpcClient } = rpc();
		const unregister = rpcClient.registerResponseInterceptor((response, request) => {
			if (response.code === 401 && request.scope === "auth" && PRE_AUTH_PROCEDURES.has(request.procedure ?? "")) return response;
			if (response.code === 401) {
				console.warn("<AuthWrapper2 /> intercepted code=401 response from request:", request);
				Alerts.error("Auth expired, please login again", void 0, { toastId: "auth-401" });
				this.clearAuth();
				this.bus.emit("authExpired", void 0);
			}
			return response;
		});
		this.addCleanup(unregister);
	}
	setupServerEventHandlers() {
		const { rpcClient } = rpc();
		const unsubscribe = rpcClient.subscribeToServerMessages((msg) => {
			const { data, event } = msg;
			switch (event) {
				case "OrganizationUpdated":
					this.refreshIdentity();
					break;
				case "UserClientMetadataChanged":
					this.set((prev) => {
						if (!prev.identityInfo) return {};
						return { identityInfo: {
							...prev.identityInfo,
							user: {
								...prev.identityInfo.user,
								userClientMetadata: data
							}
						} };
					});
					break;
				case "UserDeactivated":
					if (data.userId === this.state.identityInfo?.user?.id) this.clearAuth();
					break;
				case "UserUpdated":
				case "UserStatusChanged":
					if (data.userId === this.state.identityInfo?.user?.id) this.refreshIdentity();
					break;
			}
		});
		this.addCleanup(unsubscribe);
	}
	setupCrossTabSync() {
		const handleFocus = () => {
			let lsAuthToken = "";
			let pendingToken = "";
			try {
				lsAuthToken = useLocalStorage.getItem(LS_AUTH_TOKEN_KEY) || "";
			} catch {}
			try {
				pendingToken = useLocalStorage.getItem(LS_AUTH_TOKEN_PENDING_KEY) || "";
			} catch {}
			if (lsAuthToken === this.state.authToken) return;
			if (pendingToken && this.state.authToken === pendingToken) return;
			console.info("authToken does not match the current authToken - resetting AuthWrapper2 state.");
			this.clearAuth();
			if (lsAuthToken) {
				Alerts.info("Detected a different User Profile - Switch Profiles", { toastId: "switching-profile" });
				this.set({
					authToken: lsAuthToken,
					switchingUserProfile: true
				});
			}
		};
		window.addEventListener("focus", handleFocus);
		this.addCleanup(() => window.removeEventListener("focus", handleFocus));
	}
	clearPendingAuthToken() {
		useLocalStorage.setItem(LS_AUTH_TOKEN_PENDING_KEY, void 0);
	}
	clearAuth() {
		const { rpcClient } = rpc();
		rpcClient.setIdentity();
		rpcClient.clearCache();
		this.clearPendingAuthToken();
		this.set({
			authToken: "",
			identityInfo: null,
			switchingUserProfile: false
		});
	}
	refreshIdentity() {
		this.authService.getIdentityInfo().then((data) => {
			this.set({ identityInfo: data });
			this.silentTokenRefresh();
			this.registerDevice();
		}).catch((rpcResponse) => {
			if (!window.navigator.onLine) {
				Alerts.error("No internet connection", "Please check your internet connection");
				return;
			}
			this.clearAuth();
			console.warn("Error auth::get-identity-info", rpcResponse?.msg || rpcResponse);
		});
	}
	silentTokenRefresh() {
		if (!this.state.identityInfo?.refreshToken) return;
		this.authService.refreshAuthToken().then((data) => {
			useLocalStorage.setItem(LS_AUTH_TOKEN_PENDING_KEY, data.authToken, { silent: true });
		}).catch((err) => {
			console.error("There was a problem calling \"auth::refresh-auth-token::1\"", err.message);
		});
	}
	registerDevice() {
		this.authService.saveDeviceInfo(this.app).catch((err) => {
			console.error("Error saving device info:", err);
		});
	}
};
//#endregion
export { AuthViewModel };

//# sourceMappingURL=AuthViewModel.js.map